import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ReactTable from "react-table";
import Moment from "moment";
import "react-datetime/css/react-datetime.css";
import Datetime from "react-datetime";

import { makeStyles } from "@material-ui/core/styles";
import Assignment from "@material-ui/icons/Assignment";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select
} from "@material-ui/core";

import CustomInput from "components/CustomInput/CustomInput.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import AgentDialog from "components/AgentDialog/AgentDialog.js";

import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";

import { bookingApi, masterApi } from "functions/apis";
import { token } from "functions/Helper";

const styles = {
  cardIconTitle: { ...cardTitle, marginTop: "15px", marginBottom: "0px" }
};
const useStyles = makeStyles(styles);

export default function DailyQuotaList() {
  const source = useRef(new AbortController());
  const canWrite =
    token["booking.agent_daily_quotas.write"] === "allowed" ||
    token.is_admin === "True";

  const [data, setData] = React.useState([]);
  const [pages, setPages] = React.useState(0);
  const [reload, setReload] = React.useState(0);
  const [pagination, setPagination] = React.useState({
    pageIndex: 1,
    pageSize: 10
  });
  const [filter, setFilter] = React.useState({
    searchString: null,
    dateFrom: null,
    dateTo: null,
    agentID: null,
    routeID: null
  });
  const [filterAgent, setFilterAgent] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  // Adjust dialog (Add / Release)
  const [adjust, setAdjust] = React.useState(null); // { id, mode, seats, remarks }
  // Create dialog
  const [routes, setRoutes] = React.useState([]);
  const [create, setCreate] = React.useState(null); // { agentID, routeID, time, date, allocatedSeat, remarks }
  const [createAgent, setCreateAgent] = React.useState(null);
  // Generate dialog
  const [generate, setGenerate] = React.useState(null); // { dateFrom, dateTo, agentID }
  const [generateAgent, setGenerateAgent] = React.useState(null);
  // Bulk release dialog
  const [bulkRelease, setBulkRelease] = React.useState(null); // { dateFrom, dateTo, routeID, time, agentID, remarks }
  const [bulkReleaseAgent, setBulkReleaseAgent] = React.useState(null);
  const [bulkReleaseSubmitting, setBulkReleaseSubmitting] = React.useState(
    false
  );
  // Which target the agent picker is serving: null | "filter" | "create" | "generate" | "bulkRelease"
  const [agentModal, setAgentModal] = React.useState(null);

  const handleAgentSelected = e => {
    if (agentModal === "filter") {
      setFilter({ ...filter, agentID: e.id });
      setFilterAgent(e);
    } else if (agentModal === "create") {
      setCreate({ ...create, agentID: e.id });
      setCreateAgent(e);
    } else if (agentModal === "generate") {
      setGenerate({ ...generate, agentID: e.id });
      setGenerateAgent(e);
    } else if (agentModal === "bulkRelease") {
      setBulkRelease({ ...bulkRelease, agentID: e.id });
      setBulkReleaseAgent(e);
    }
    setAgentModal(null);
  };

  const openGenerate = () => {
    setGenerate({ dateFrom: "", dateTo: "", agentID: "" });
    setGenerateAgent(null);
  };

  const submitGenerate = () => {
    bookingApi
      .post(
        "/AgentDailyQuotas/Generate",
        {
          dateFrom: generate.dateFrom
            ? Moment(generate.dateFrom).format("YYYY-MM-DD")
            : null,
          dateTo: generate.dateTo
            ? Moment(generate.dateTo).format("YYYY-MM-DD")
            : null,
          agentID: generate.agentID || null
        },
        { signal: source.current.signal }
      )
      .then(() => {
        setGenerate(null);
        alert("Generation queued. It runs in the background.");
      })
      .catch(error => console.log(error));
  };

  const openBulkRelease = () => {
    setBulkRelease({
      dateFrom: "",
      dateTo: "",
      routeID: "",
      time: "",
      agentID: "",
      remarks: ""
    });
    setBulkReleaseAgent(null);
  };

  const submitBulkRelease = () => {
    if (
      !bulkRelease.dateFrom ||
      !bulkRelease.dateTo ||
      !bulkRelease.routeID ||
      !bulkRelease.time
    ) {
      alert("Date From, Date Until, Route and Time are required.");
      return;
    }
    const route = routes.find(r => r.id === bulkRelease.routeID);
    if (
      !window.confirm(
        "Release ALL remaining seats for route " +
          (route ? route.code : "?") +
          ", time " +
          bulkRelease.time +
          ", " +
          Moment(bulkRelease.dateFrom).format("YYYY-MM-DD") +
          " to " +
          Moment(bulkRelease.dateTo).format("YYYY-MM-DD") +
          ", " +
          (bulkReleaseAgent ? "agent " + bulkReleaseAgent.code : "ALL agents") +
          "?"
      )
    )
      return;
    setBulkReleaseSubmitting(true);
    bookingApi
      .post(
        "/AgentDailyQuotas/BulkRelease",
        {
          dateFrom: Moment(bulkRelease.dateFrom).format("YYYY-MM-DD"),
          dateTo: Moment(bulkRelease.dateTo).format("YYYY-MM-DD"),
          routeID: bulkRelease.routeID,
          time: bulkRelease.time,
          agentID: bulkRelease.agentID || null,
          remarks: bulkRelease.remarks
        },
        { signal: source.current.signal }
      )
      .then(data => {
        setBulkRelease(null);
        setReload(r => r + 1);
        alert(
          "Queued release of " +
            data.totalSeats +
            " seat(s) across " +
            data.totalQuotas +
            " quota row(s). Processing runs in the background."
        );
      })
      .catch(error => console.log(error))
      .finally(() => setBulkReleaseSubmitting(false));
  };

  useEffect(() => {
    masterApi
      .get("/Routes", {
        signal: source.current.signal,
        params: {
          filter: { searchString: "", publicationStatus: 1, sort: 0 },
          pagination: { pageIndex: 0, pageSize: 0 }
        }
      })
      .then(data => setRoutes(data.records))
      .catch(error => console.log(error));

    return () => source.current.abort();
  }, []);

  useEffect(() => {
    setLoading(true);
    bookingApi
      .get("/AgentDailyQuotas", {
        signal: source.current.signal,
        params: {
          filter: {
            ...filter,
            dateFrom: filter.dateFrom
              ? Moment(filter.dateFrom).format("YYYY-MM-DD")
              : null,
            dateTo: filter.dateTo
              ? Moment(filter.dateTo).format("YYYY-MM-DD")
              : null
          },
          pagination
        }
      })
      .then(data => {
        setData(
          data.records.map(record => ({
            ...record,
            agentCode: record.agent?.code,
            routeCode: record.route?.code,
            dateLabel: record.date?.substring(0, 10),
            actions: (
              <div className="actions-right">
                {canWrite && (
                  <Button
                    color="success"
                    className="like"
                    onClick={() =>
                      setAdjust({
                        id: record.id,
                        mode: "add",
                        seats: 0,
                        remarks: ""
                      })
                    }
                  >
                    ADD
                  </Button>
                )}
                {canWrite && (
                  <Button
                    color="warning"
                    className="like"
                    onClick={() =>
                      setAdjust({
                        id: record.id,
                        mode: "release",
                        seats: 0,
                        remarks: ""
                      })
                    }
                  >
                    RELEASE
                  </Button>
                )}
                <Link to={"/admin/AgentDailyQuotas/Detail/" + record.id}>
                  <Button color="info" className="like">
                    VIEW
                  </Button>
                </Link>
              </div>
            )
          }))
        );
        setPages(Math.ceil(data.totalRecords / pagination.pageSize));
        setLoading(false);
      })
      .catch(error => console.log(error));
  }, [pagination, filter, reload]);

  const submitAdjust = () => {
    const url =
      adjust.mode === "add"
        ? "/AgentDailyQuotas/AddSeat"
        : "/AgentDailyQuotas/ReleaseSeat";
    bookingApi
      .post(
        url,
        { id: adjust.id, seats: adjust.seats, remarks: adjust.remarks },
        { signal: source.current.signal }
      )
      .then(() => {
        setAdjust(null);
        setReload(r => r + 1);
      })
      .catch(error => console.log(error));
  };

  const openCreate = () => {
    setCreate({
      agentID: "",
      routeID: "",
      time: "",
      date: "",
      allocatedSeat: 0,
      remarks: ""
    });
    setCreateAgent(null);
  };

  const submitCreate = () => {
    bookingApi
      .post(
        "/AgentDailyQuotas",
        {
          ...create,
          date: create.date ? Moment(create.date).format("YYYY-MM-DD") : null
        },
        { signal: source.current.signal }
      )
      .then(() => {
        setCreate(null);
        setReload(r => r + 1);
      })
      .catch(error => console.log(error));
  };

  const classes = useStyles();
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>AGENT DAILY QUOTA</h4>
          </CardHeader>
          <CardBody>
            {canWrite && (
              <Button color="rose" onClick={openCreate}>
                CREATE MANUAL
              </Button>
            )}
            {canWrite && (
              <Button color="info" onClick={openGenerate}>
                GENERATE
              </Button>
            )}
            {canWrite && (
              <Button color="warning" onClick={openBulkRelease}>
                BULK RELEASE
              </Button>
            )}
            <GridContainer>
              <GridItem xs={4}>
                <CustomInput
                  labelText="Search"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    onChange: e =>
                      setFilter({
                        ...filter,
                        searchString:
                          e.target.value === "" ? null : e.target.value
                      }),
                    value:
                      filter.searchString === null ? "" : filter.searchString
                  }}
                />
              </GridItem>
              <GridItem xs={4}>
                <FormControl fullWidth>
                  <Datetime
                    value={filter.dateFrom}
                    onChange={e =>
                      setFilter({
                        ...filter,
                        dateFrom: e === "" ? null : e
                      })
                    }
                    timeFormat={false}
                    closeOnSelect={true}
                    inputProps={{ placeholder: "Date From" }}
                  />
                </FormControl>
              </GridItem>
              <GridItem xs={4}>
                <FormControl fullWidth>
                  <Datetime
                    value={filter.dateTo}
                    onChange={e =>
                      setFilter({
                        ...filter,
                        dateTo: e === "" ? null : e
                      })
                    }
                    timeFormat={false}
                    closeOnSelect={true}
                    inputProps={{ placeholder: "Date To" }}
                  />
                </FormControl>
              </GridItem>
              <GridItem xs={12} md={6}>
                <FormControl
                  fullWidth
                  onClick={() => setAgentModal("filter")}
                  style={{ cursor: "pointer" }}
                >
                  <InputLabel style={{ pointerEvents: "none" }}>
                    Agent
                  </InputLabel>
                  <Input
                    value={
                      filterAgent
                        ? filterAgent.code + " - " + filterAgent.name
                        : ""
                    }
                    disabled
                    style={{ pointerEvents: "none" }}
                  />
                </FormControl>
                {filterAgent !== null && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setFilterAgent(null);
                      setFilter({ ...filter, agentID: null });
                    }}
                  >
                    All Agents
                  </Button>
                )}
              </GridItem>
              <GridItem xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Route</InputLabel>
                  <Select
                    value={filter.routeID ?? ""}
                    onChange={e =>
                      setFilter({
                        ...filter,
                        routeID: e.target.value === "" ? null : e.target.value
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>All Routes</em>
                    </MenuItem>
                    {routes.map(r => (
                      <MenuItem value={r.id} key={r.id}>
                        <b>{r.code}</b>&nbsp;{r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({ ...pagination, pageIndex: page + 1 })
              }
              onPageSizeChange={(pageSize, page) =>
                setPagination({ pageSize, pageIndex: page + 1 })
              }
              loading={loading}
              data={data}
              filterable={false}
              columns={[
                { Header: "Agent", accessor: "agentCode" },
                { Header: "Route", accessor: "routeCode" },
                { Header: "Date", accessor: "dateLabel" },
                { Header: "Time", accessor: "time" },
                { Header: "Remaining", accessor: "remaining" },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>

      {/* Add / Release dialog */}
      <Dialog open={adjust !== null} onClose={() => setAdjust(null)}>
        <DialogTitle>
          {adjust?.mode === "add" ? "Add Seats" : "Release Seats"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Seats"
            type="number"
            fullWidth
            value={adjust?.seats ?? 0}
            onChange={e =>
              setAdjust({
                ...adjust,
                seats: parseInt(e.target.value || "0", 10)
              })
            }
          />
          <TextField
            label="Remarks"
            fullWidth
            value={adjust?.remarks ?? ""}
            onChange={e => setAdjust({ ...adjust, remarks: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjust(null)}>Cancel</Button>
          <Button color="rose" onClick={submitAdjust}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create manual dialog */}
      <Dialog
        open={create !== null}
        onClose={() => setCreate(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { overflowY: "visible" } }}
      >
        <DialogTitle>Create Manual Quota</DialogTitle>
        <DialogContent style={{ overflowY: "visible" }}>
          <FormControl
            fullWidth
            onClick={() => setAgentModal("create")}
            style={{ cursor: "pointer" }}
          >
            <InputLabel style={{ pointerEvents: "none" }}>Agent</InputLabel>
            <Input
              value={
                createAgent ? createAgent.code + " - " + createAgent.name : ""
              }
              disabled
              style={{ pointerEvents: "none" }}
            />
          </FormControl>
          <FormControl fullWidth style={{ marginTop: 16 }}>
            <InputLabel>Route</InputLabel>
            <Select
              value={create?.routeID ?? ""}
              onChange={e => setCreate({ ...create, routeID: e.target.value })}
            >
              {routes.map(r => (
                <MenuItem value={r.id} key={r.id}>
                  <b>{r.code}</b>&nbsp;{r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Time (hhmm, e.g. 0900)"
            fullWidth
            style={{ marginTop: 16 }}
            value={create?.time ?? ""}
            onChange={e => setCreate({ ...create, time: e.target.value })}
          />
          <FormControl fullWidth style={{ marginTop: 16 }}>
            <Datetime
              value={create?.date}
              onChange={e =>
                setCreate({ ...create, date: e === "" ? null : e })
              }
              timeFormat={false}
              closeOnSelect={true}
              inputProps={{ placeholder: "Date" }}
            />
          </FormControl>
          <TextField
            label="Allocated Seat"
            type="number"
            fullWidth
            style={{ marginTop: 16 }}
            value={create?.allocatedSeat ?? 0}
            onChange={e =>
              setCreate({
                ...create,
                allocatedSeat: parseInt(e.target.value || "0", 10)
              })
            }
          />
          <TextField
            label="Remarks"
            fullWidth
            style={{ marginTop: 16 }}
            value={create?.remarks ?? ""}
            onChange={e => setCreate({ ...create, remarks: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreate(null)}>Cancel</Button>
          <Button color="rose" onClick={submitCreate}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={generate !== null}
        onClose={() => setGenerate(null)}
        PaperProps={{ style: { overflowY: "visible" } }}
      >
        <DialogTitle>Generate Daily Quota</DialogTitle>
        <DialogContent style={{ minWidth: 360, overflowY: "visible" }}>
          <p>
            Creates any missing quota rows for the selected window. Runs in the
            background; existing rows are left unchanged.
          </p>
          <FormControl fullWidth>
            <Datetime
              value={generate?.dateFrom}
              onChange={e =>
                setGenerate({ ...generate, dateFrom: e === "" ? null : e })
              }
              timeFormat={false}
              closeOnSelect={true}
              inputProps={{ placeholder: "Date From" }}
            />
          </FormControl>
          <FormControl fullWidth style={{ marginTop: 16 }}>
            <Datetime
              value={generate?.dateTo}
              onChange={e =>
                setGenerate({ ...generate, dateTo: e === "" ? null : e })
              }
              timeFormat={false}
              closeOnSelect={true}
              inputProps={{ placeholder: "Date To" }}
            />
          </FormControl>
          <FormControl
            fullWidth
            onClick={() => setAgentModal("generate")}
            style={{ cursor: "pointer", marginTop: 16 }}
          >
            <InputLabel shrink style={{ pointerEvents: "none" }}>
              Agent (optional — all if blank)
            </InputLabel>
            <Input
              value={
                generateAgent
                  ? generateAgent.code + " - " + generateAgent.name
                  : "All agents"
              }
              disabled
              style={{ pointerEvents: "none" }}
            />
          </FormControl>
          {generateAgent !== null && (
            <Button
              size="sm"
              onClick={() => {
                setGenerateAgent(null);
                setGenerate({ ...generate, agentID: "" });
              }}
            >
              All agents
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerate(null)}>Cancel</Button>
          <Button color="info" onClick={submitGenerate}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkRelease !== null}
        onClose={() => setBulkRelease(null)}
        PaperProps={{ style: { overflowY: "visible" } }}
      >
        <DialogTitle>Bulk Release Daily Quota</DialogTitle>
        <DialogContent style={{ minWidth: 360, overflowY: "visible" }}>
          <p>
            Releases ALL remaining seats on every quota row matching the window,
            route and time back to Core API. Runs in the background.
          </p>
          <FormControl fullWidth>
            <Datetime
              value={bulkRelease?.dateFrom}
              onChange={e =>
                setBulkRelease({
                  ...bulkRelease,
                  dateFrom: e === "" ? null : e
                })
              }
              timeFormat={false}
              closeOnSelect={true}
              inputProps={{ placeholder: "Date From" }}
            />
          </FormControl>
          <FormControl fullWidth style={{ marginTop: 16 }}>
            <Datetime
              value={bulkRelease?.dateTo}
              onChange={e =>
                setBulkRelease({
                  ...bulkRelease,
                  dateTo: e === "" ? null : e
                })
              }
              timeFormat={false}
              closeOnSelect={true}
              inputProps={{ placeholder: "Date Until" }}
            />
          </FormControl>
          <FormControl fullWidth style={{ marginTop: 16 }}>
            <InputLabel>Route</InputLabel>
            <Select
              value={bulkRelease?.routeID ?? ""}
              onChange={e =>
                setBulkRelease({ ...bulkRelease, routeID: e.target.value })
              }
            >
              {routes.map(r => (
                <MenuItem value={r.id} key={r.id}>
                  <b>{r.code}</b>&nbsp;{r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Time (hhmm, e.g. 0900)"
            fullWidth
            style={{ marginTop: 16 }}
            value={bulkRelease?.time ?? ""}
            onChange={e =>
              setBulkRelease({ ...bulkRelease, time: e.target.value })
            }
          />
          <FormControl
            fullWidth
            onClick={() => setAgentModal("bulkRelease")}
            style={{ cursor: "pointer", marginTop: 16 }}
          >
            <InputLabel shrink style={{ pointerEvents: "none" }}>
              Agent (optional — all if blank)
            </InputLabel>
            <Input
              value={
                bulkReleaseAgent
                  ? bulkReleaseAgent.code + " - " + bulkReleaseAgent.name
                  : "All agents"
              }
              disabled
              style={{ pointerEvents: "none" }}
            />
          </FormControl>
          {bulkReleaseAgent !== null && (
            <Button
              size="sm"
              onClick={() => {
                setBulkReleaseAgent(null);
                setBulkRelease({ ...bulkRelease, agentID: "" });
              }}
            >
              All agents
            </Button>
          )}
          <TextField
            label="Remarks"
            fullWidth
            style={{ marginTop: 16 }}
            value={bulkRelease?.remarks ?? ""}
            onChange={e =>
              setBulkRelease({ ...bulkRelease, remarks: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRelease(null)}>Cancel</Button>
          <Button
            color="warning"
            disabled={bulkReleaseSubmitting}
            onClick={submitBulkRelease}
          >
            Release
          </Button>
        </DialogActions>
      </Dialog>

      <AgentDialog
        openModal={agentModal !== null}
        setOpenModal={() => setAgentModal(null)}
        onSelect={handleAgentSelected}
      />
    </GridContainer>
  );
}
